// Content for the Compteurs d'eau ATNA « En production » (Casselman) section.
// Research figures are EXTERNAL benchmarks, not Casselman or BCITI | ATNA results.
// Dashboard values (reused from the solution dashboard) are illustrative demo data.
export type Bi = { fr: string; en: string }

export const WATER_PRODUCTION_IMAGES = {
  // Casselman water tower, cropped from the supplied reference image.
  casselman: 'https://cdn.magicpatterns.com/uploads/8ZokTnh5eCrqt2DCzELarB/Image_ChatGPT_27_sept._2026,_22_49_45.png',
  meter: 'https://cdn.magicpatterns.com/patterns/generated-images/58d48db4-fc00-4933-8ba2-02b4252b18b6.jpg',
}

export const productionCopy = {
  eyebrow: { fr: 'En production', en: 'In production' },
  navy: { fr: 'Déployé', en: 'Deployed' },
  indigo: { fr: 'à Casselman.', en: 'in Casselman.' },
  bodyStrong1: { fr: 'De la donnée du compteur', en: 'From meter data' },
  bodyMid: { fr: ' jusqu’à l’analyse, ', en: ' to insight, ' },
  bodyStrong2: { fr: 'la notification et l’action', en: 'notification and action' },
  bodyEnd: {
    fr: ' — dans une infrastructure reliée aux identités et aux propriétés.',
    en: ' — on infrastructure connected to identities and properties.',
  },
  towerAlt: {
    fr: 'Château d’eau de Casselman, en Ontario, surplombant la municipalité',
    en: 'Casselman water tower in Ontario, overlooking the town',
  },
  meterAlt: { fr: 'Compteur d’eau résidentiel connecté', en: 'Connected residential water meter' },
  disclaimer: {
    fr: '* Résultats issus d’études externes sur les compteurs intelligents, la détection des fuites et la rétroaction de consommation. Les résultats varient selon le déploiement, l’infrastructure et la participation des usagers.',
    en: '* Findings from external studies on smart meters, leak detection and consumption feedback. Results vary by deployment, infrastructure and user participation.',
  },
  researchTag: { fr: 'Étude externe', en: 'External study' },
}

export const researchBenchmarks = [
  { key: 'leak', value: { fr: '89 %', en: '89%' }, text: { fr: 'de réduction des pertes liées aux fuites*', en: 'reduction in leak-related losses*' } },
  { key: 'repair', value: { fr: '29 → 19 jours', en: '29 → 19 days' }, text: { fr: 'pour résoudre une fuite (−34 %)*', en: 'to resolve a leak (−34%)*' } },
  { key: 'usage', value: { fr: '5 à 15 %', en: '5–15%' }, text: { fr: 'de consommation en moins en moyenne*', en: 'lower consumption on average*' } },
] as { key: 'leak' | 'repair' | 'usage'; value: Bi; text: Bi }[]

export const casselmanProof = {
  name: { fr: 'Casselman', en: 'Casselman' },
  region: { fr: 'Ontario', en: 'Ontario' },
  text: { fr: 'Compteurs d’eau intelligents en production', en: 'Smart water meters in production' },
}

export type CapabilityKey = 'data' | 'identity' | 'insight' | 'notify' | 'pay' | 'act'

export const capabilities: { key: CapabilityKey; title: Bi; text: Bi }[] = [
  {
    key: 'data',
    title: { fr: 'Données de compteurs intégrées', en: 'Integrated meter data' },
    text: {
      fr: 'Capteurs, IoT, API ou prise de photo : les données sont centralisées dans une même expérience.',
      en: 'Sensors, IoT, APIs or photo capture: data is centralized in a single experience.',
    },
  },
  {
    key: 'identity',
    title: { fr: 'Identité et propriétés reliées', en: 'Linked identity & properties' },
    text: {
      fr: 'Une identité vérifiée peut être associée à une ou plusieurs propriétés et à leurs compteurs respectifs.',
      en: 'A verified identity can be linked to one or more properties and their respective meters.',
    },
  },
  {
    key: 'insight',
    title: { fr: 'Consommation et analyses intelligentes', en: 'Consumption & smart insights' },
    text: {
      fr: 'ATNA analyse la consommation, détecte les fuites, les surconsommations et les usages inhabituels, puis fait ressortir les situations qui méritent une attention.',
      en: 'ATNA analyzes consumption, detects leaks, overconsumption and unusual usage, then surfaces the situations that deserve attention.',
    },
  },
  {
    key: 'notify',
    title: { fr: 'Notifications préventives', en: 'Preventive notifications' },
    text: {
      fr: 'Alertes de consommation, fuites potentielles, seuils atteints et rappels réglementaires transmis aux bonnes personnes.',
      en: 'Consumption alerts, potential leaks, thresholds reached and by-law reminders sent to the right people.',
    },
  },
  {
    key: 'pay',
    title: { fr: 'Paiements intégrés', en: 'Integrated payments' },
    text: {
      fr: 'Les dépassements ou frais applicables peuvent être présentés à l’usager et payés directement dans son expérience numérique.',
      en: 'Overages or applicable fees can be presented to the user and paid directly in their digital experience.',
    },
  },
  {
    key: 'act',
    title: { fr: 'Actions et suivi', en: 'Actions & follow-up' },
    text: {
      fr: 'ATNA peut déclencher les flux de travail autorisés : notification, demande, intervention, paiement et suivi jusqu’à la résolution.',
      en: 'ATNA can trigger authorized workflows: notification, request, work order, payment and follow-up to resolution.',
    },
  },
]

export const capabilityVisuals = {
  identity: {
    person: { fr: 'Marie Dubois', en: 'Marie Dubois' },
    verified: { fr: 'Identité vérifiée', en: 'Verified identity' },
    properties: [
      { address: { fr: '123 rue Principale', en: '123 Main St' }, meters: { fr: '2 compteurs d’eau', en: '2 water meters' } },
      { address: { fr: '45 rue des Érables', en: '45 Maple St' }, meters: { fr: '1 compteur d’eau', en: '1 water meter' } },
    ],
  },
  insight: {
    title: { fr: 'Consommation inhabituelle', en: 'Unusual consumption' },
    value: { fr: '1 840 L aujourd’hui', en: '1,840 L today' },
    trend: { fr: '+32 % par rapport à la moyenne', en: '+32% vs average' },
    bars: [30, 42, 58, 50, 64, 60, 96],
  },
  notify: {
    tag: { fr: 'Notification', en: 'Notification' },
    time: { fr: '10 h 24', en: '10:24 a.m.' },
    title: { fr: 'Consommation élevée détectée', en: 'High consumption detected' },
    text: {
      fr: 'à votre propriété du 123 rue Principale. Vérifiez l’arrosage extérieur.',
      en: 'at your property at 123 Main St. Check your outdoor watering.',
    },
  },
  pay: {
    title: { fr: 'Dépassement détecté', en: 'Overage detected' },
    amount: { fr: 'Montant dû : 18,40 $', en: 'Amount due: $18.40' },
    cta: { fr: 'Payer maintenant', en: 'Pay now' },
  },
  act: [
    { key: 'created', title: { fr: 'Demande créée', en: 'Request created' }, sub: { fr: '#2026-4873', en: '#2026-4873' } },
    { key: 'assigned', title: { fr: 'Intervention assignée', en: 'Work order assigned' }, sub: { fr: 'Service des travaux publics', en: 'Public Works' } },
    { key: 'tracking', title: { fr: 'Suivi en cours', en: 'Follow-up in progress' }, sub: { fr: 'Mise à jour : aujourd’hui', en: 'Updated: today' } },
  ] as { key: 'created' | 'assigned' | 'tracking'; title: Bi; sub: Bi }[],
}
